<script>
  import ArrayEditor from './ArrayEditor.svelte';
  
  export let question;
  export let updateWorkingQuestion;
  export let hasUnsavedChanges;
  export let questionType = question.kind;

  let availableQuestions, suggestionsType, currentReference;

  // Define the dynamic reference patterns
  const DYNAMIC_REFERENCES = [
    '__datasetNodeMatches__',
    '__priorAnswers__', 
    '__priorAnswer__',
    /^__answer\d+__$/,
    /^__answers\d+__$/,
    /^__manyAnswers\d+__$/
  ];

  // Check if a value is a dynamic reference
  function isDynamicReference(value) {
    if (typeof value !== 'string') return false;
    
    return DYNAMIC_REFERENCES.some(ref => {
      if (typeof ref === 'string') {
        return value === ref;
      } else if (ref instanceof RegExp) {
        return ref.test(value);
      }
      return false;
    });
  }
  
  // Get available questions for reference
  function getAvailableQuestions() {
    const currentQuestionIndex = parseInt(question.id?.split('--')[1]) || 0;
    const questions = [];
    
    for (let i = 0; i < currentQuestionIndex; i++) {
      questions.push({
        index: i,
        id: `question--${i}`,
        label: `Question ${i}`,
        singleValue: `__answer${i}__`,
        allValue: `__answers${i}__`,
        manyValue: `__manyAnswers${i}__`
      });
    }
    
    return questions;
  }
  
  function getSuggestionsType() {
    // Check if suggestions is undefined or null
    if (!question.suggestions) return 'none';
    
    // Check if it's an array
    if (Array.isArray(question.suggestions)) {
      // If array has one item that's a dynamic reference, it's a reference type
      if (question.suggestions.length === 1 && isDynamicReference(question.suggestions[0])) {
        return 'reference';
      }
      // Otherwise it's custom
      return 'custom';
    }
    
    // Check if it's a string (old format reference)
    if (typeof question.suggestions === 'string' && isDynamicReference(question.suggestions)) {
      return 'reference';
    }
    
    // Fallback to custom
    return 'custom';
  }
  
  // Get the actual reference value from the suggestions array
  function getReferenceValue() {
    if (Array.isArray(question.suggestions) && question.suggestions.length > 0) {
      return question.suggestions[0];
    }
    return '__datasetNodeMatches__'; // default
  }
  
  function setSuggestionsType(type) {
    switch (type) {
      case 'none':
        updateWorkingQuestion('suggestions', undefined);
        break;
      case 'custom':
        updateWorkingQuestion('suggestions', ['Option 1', 'Option 2', 'Option 3']);
        break;
      case 'reference':
        updateWorkingQuestion('suggestions', ['__datasetNodeMatches__']);
        break;
    }
    hasUnsavedChanges = true;
  }
  
  function updateCustomSuggestions(newSuggestions) {
    updateWorkingQuestion('suggestions', newSuggestions);
    hasUnsavedChanges = true;
  }
  
  function updateReferenceSuggestions(reference) {
    // Store reference in array format
    updateWorkingQuestion('suggestions', [reference]);
    hasUnsavedChanges = true;
  }
  
  // Common reference options
  const commonReferences = [
    { value: '__datasetNodeMatches__', label: 'Matching Nodes (Auto-complete from existing network data)' },
    { value: '__priorAnswers__', label: 'Previous Question (All Answers)' },
    { value: '__priorAnswer__', label: 'Previous Question (Single Answer)' }
  ];
  
  // Reactive statements - these update automatically when question changes
  $: question, availableQuestions = getAvailableQuestions();
  $: question, suggestionsType = getSuggestionsType();
  $: question, currentReference = getReferenceValue();
</script>

<div class="suggestions-editor">
  <div class="suggestions-header">
    <label>Suggestions Configuration</label>
    <span class="suggestions-type-badge" class:none={suggestionsType === 'none'} class:reference={suggestionsType === 'reference'} class:custom={suggestionsType === 'custom'}>
      {suggestionsType.toUpperCase()}
    </span>
  </div>
  
  <!-- Suggestions Type Selector -->
  <div class="suggestions-type-selector">
    <div class="type-options">
      <div class="radio-option">
        <input 
          type="radio" 
          id="suggestionsType-none"
          name="suggestionsType" 
          value="none"
          checked={suggestionsType === 'none'}
          on:change={() => setSuggestionsType('none')}
        />
        <label for="suggestionsType-none" class="radio-content">
          <span class="radio-label">No Suggestions</span>
          <span class="radio-description">Users must type everything manually</span>
        </label>
      </div>
      
      <div class="radio-option">
        <input 
          type="radio" 
          id="suggestionsType-reference"
          name="suggestionsType" 
          value="reference"
          checked={suggestionsType === 'reference'}
          on:change={() => setSuggestionsType('reference')}
        />
        <label for="suggestionsType-reference" class="radio-content">
          <span class="radio-label">Dynamic Reference</span>
          <span class="radio-description">Auto-complete from other questions or network data</span>
        </label>
      </div>
      
      <div class="radio-option">
        <input 
          type="radio" 
          id="suggestionsType-custom"
          name="suggestionsType" 
          value="custom"
          checked={suggestionsType === 'custom'}
          on:change={() => setSuggestionsType('custom')}
        />
        <label for="suggestionsType-custom" class="radio-content">
          <span class="radio-label">Custom List</span>
          <span class="radio-description">Predefined list of options</span>
        </label>
      </div>
    </div>
  </div>
  
  <!-- Reference Configuration -->
  {#if suggestionsType === 'reference'}
    <div class="reference-configuration">
      <label>Reference Source</label>
      <select 
        value={currentReference}
        on:change={(e) => updateReferenceSuggestions(e.target.value)}
        class="reference-select"
      >
        <optgroup label="Common References">
          {#each commonReferences as ref}
            <option value={ref.value}>{ref.label}</option>
          {/each}
        </optgroup>
        
        {#if availableQuestions.length > 0}
          <optgroup label="Specific Questions">
            {#each availableQuestions as q}
              <option value={q.allValue}>Question {q.index} (All Answers)</option>
              <option value={q.singleValue}>Question {q.index} (Single Answer)</option>
              <option value={q.manyValue}>Question {q.index} (Many Answers)</option>
            {/each}
          </optgroup>
        {/if}
      </select>
      
      <div class="reference-preview">
        <span class="preview-label">Current Reference:</span>
        <code class="preview-value">{currentReference}</code>
      </div>
    </div>
  {/if}
  
  <!-- Custom List Configuration -->
  {#if suggestionsType === 'custom'}
    <div class="custom-configuration">
      <label>Custom Suggestions</label>
      <ArrayEditor 
        bind:items={question.suggestions}
        placeholder="Suggestion option"
        minItems={1}
        on:update={(e) => updateCustomSuggestions(e.detail)}
      />
    </div>
  {/if}
  
  <!-- Additional Settings -->
  {#if suggestionsType !== 'none' && (questionType === 'Select' || questionType === 'Text')}
    <div class="additional-settings">
      
      {#if questionType === 'Text'}
        <div class="settings-row">
          <label class="checkbox-item">
            
            Validate against location names
          </label>
          <input 
              type="checkbox"
              checked={question.checkLocationName === true}
              on:change={(e) => {
                updateWorkingQuestion('checkLocationName', e.target.checked);
                hasUnsavedChanges = true;
              }}
            />
        </div>
      {/if}
    </div>
  {/if}
  
  <!-- Help Text -->
  <div class="suggestions-help">
    {#if suggestionsType === 'none'}
      <p>No auto-complete suggestions will be shown to users.</p>
    {:else if suggestionsType === 'reference'}
      <p>Suggestions will be dynamically loaded from the specified reference source.</p>
    {:else if suggestionsType === 'custom'}
      <p>Users will see the custom list as auto-complete suggestions.</p>
    {/if}
  </div>
</div>

<style lang="scss">
  .suggestions-editor {
    border: 2px solid #e5e7eb;
    border-radius: 8px;
    padding: 15px;
    margin-bottom: 15px;
    background: rgba(255, 255, 255, 0.9);
  }
  
  .suggestions-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 15px;
    
    label {
      font-weight: bold;
      font-size: 0.85rem;
      color: #374151;
    }
  }
  
  .suggestions-type-badge {
    padding: 2px 8px;
    border-radius: 12px;
    font-size: 10px;
    font-weight: bold;
    letter-spacing: 0.5px;
    
    &.none {
      background: #6b7280;
      color: white;
    }
    
    &.reference {
      background: #3b82f6;
      color: white;
    }
    
    &.custom {
      background: #10b981;
      color: white;
    }
  }
  
  .suggestions-type-selector {
    margin-bottom: 20px;
    
    .type-options {
      display: flex;
      flex-direction: column;
      gap: 8px;
    }
    
    .radio-option {
      display: flex;
      align-items: flex-start;
      padding: 10px 12px;
      border: 1px solid #e5e7eb;
      border-radius: 6px;
      transition: all 0.2s ease;
      background: #fff;
      position: relative;
      
      &:hover {
        border-color: #d1d5db;
        background: #f9fafb;
      }
      
      input[type="radio"] {
        position: absolute;
        opacity: 0;
        width: 0;
        height: 0;
        
        &:checked ~ .radio-content::before {
          background-color: #3b82f6;
          border-color: #3b82f6;
        }
        
        &:checked ~ .radio-content::after {
          opacity: 1;
        }
        
        &:checked ~ .radio-content .radio-label {
          color: #1e40af;
        }
      }
      
      &:has(input[type="radio"]:checked) {
        border-color: #3b82f6;
        background: #eff6ff;
      }
      
      .radio-content {
        display: flex;
        flex-direction: column;
        gap: 2px;
        padding-left: 28px;
        position: relative;
        cursor: pointer;
        width: 100%;
        
        &::before {
          content: '';
          position: absolute;
          left: 0;
          top: 2px;
          width: 16px;
          height: 16px;
          border: 2px solid #d1d5db;
          border-radius: 50%;
          background: white;
          transition: all 0.2s ease;
        }
        
        &::after {
          content: '';
          position: absolute;
          left: 5px;
          top: 7px;
          width: 6px;
          height: 6px;
          border-radius: 50%;
          background: white;
          opacity: 0;
          transition: opacity 0.2s ease;
        }
      }
      
      .radio-label {
        font-weight: 600;
        font-size: 0.8rem;
        color: #374151;
        transition: color 0.2s ease;
      }
      
      .radio-description {
        font-size: 0.7rem;
        color: #6b7280;
        font-style: italic;
      }
    }
  }
  
  .reference-configuration {
    background: #eff6ff;
    border: 1px solid #93c5fd;
    border-radius: 6px;
    padding: 15px;
    margin-bottom: 15px;
    
    label {
      display: block;
      font-weight: bold;
      margin-bottom: 8px;
      font-size: 0.75rem;
      color: #1e40af;
    }
    
    .reference-select {
      width: 100%;
      border: 1px solid #93c5fd;
      border-radius: 4px;
      background: #fff;
      font-size: 0.75rem;
      font-family: monospace;
      margin-bottom: 10px;
      
      &:focus {
        outline: none;
        border-color: #2563eb;
        box-shadow: 0 0 0 2px rgba(37, 99, 235, 0.2);
      }
    }
    
    .reference-preview {
      display: flex;
      align-items: center;
      gap: 8px;
      
      .preview-label {
        font-size: 0.7rem;
        color: #1e40af;
        font-weight: 500;
      }
      
      .preview-value {
        background: #dbeafe;
        padding: 2px 6px;
        border-radius: 3px;
        font-family: monospace;
        font-size: 0.7rem;
        color: #1e40af;
      }
    }
  }
  
  .custom-configuration {
    background: #ecfdf5;
    border: 1px solid #86efac;
    border-radius: 6px;
    padding: 15px;
    margin-bottom: 15px;
    
    label {
      display: block;
      font-weight: bold;
      margin-bottom: 8px;
      font-size: 0.75rem;
      color: #059669;
    }
  }
  
  .additional-settings {
    background: #fefce8;
    border: 1px solid #fde047;
    border-radius: 6px;
    padding: 12px;
    margin-bottom: 15px;
    
    .settings-row {
      margin-bottom: 0;
      display: flex;
      flex-direction: row;
      justify-content: space-between;
      align-items: center;
      
      &:last-child {
        margin-bottom: 0;
      }
    }
    
    .checkbox-item {
      cursor: pointer;
      font-size: 0.75rem;
      color: #713f12;
      
      input[type="checkbox"] {
        margin-right: 8px;
        cursor: pointer;
      }
    }
  }
  
  .suggestions-help {
    font-size: 0.7rem;
    color: #6b7280;
    font-style: italic;
    
    p {
      margin: 0;
    }
  }
</style>